import React from 'react';
import { AlertCircle, ArrowRight, ArrowUpRight, Check, Circle, LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SITE_EYEBROW } from '../data/site';

export function Reveal({ children, className = '', delay = 0, ...props }) {
  return (
    <div
      className={`alt4-reveal${className ? ` ${className}` : ''}`}
      style={delay ? { '--alt4-reveal-delay': `${delay}s` } : undefined}
      {...props}
    >
      {children}
    </div>
  );
}

export function SectionIntro({
  eyebrow = SITE_EYEBROW,
  title,
  body,
  description,
  align = 'left',
  inverse = false,
  className = '',
}) {
  return (
    <div className={`alt4-section-intro alt4-section-intro--${align}${inverse ? ' alt4-section-intro--inverse' : ''}${className ? ` ${className}` : ''}`}>
      {eyebrow && <p className="alt4-eyebrow">{eyebrow}</p>}
      {title && <h2>{title}</h2>}
      {(body || description) && <p className="alt4-section-copy">{body || description}</p>}
    </div>
  );
}

export function PrimaryCta({ children = 'Book a walkthrough', className = '', to = '/alt4/walkthrough' }) {
  return (
    <Link className={`alt4-button${className ? ` ${className}` : ''}`} to={to}>
      {children}
      <ArrowUpRight size={18} strokeWidth={2} aria-hidden="true" />
    </Link>
  );
}

export function TextLink({ to, children, className = '' }) {
  return (
    <Link className={`alt4-text-link${className ? ` ${className}` : ''}`} to={to}>
      {children}
      <ArrowRight size={17} strokeWidth={2} aria-hidden="true" />
    </Link>
  );
}

const statusIcons = {
  blue: LoaderCircle,
  running: LoaderCircle,
  green: Check,
  complete: Check,
  completed: Check,
  review: AlertCircle,
  warning: AlertCircle,
  neutral: Circle,
};

export function StatusPill({ children, status, tone = 'blue', className = '' }) {
  const label = children || status || 'Ready';
  const normalizedTone = status && tone === 'blue' ? status.toLowerCase().replace(/\s+/g, '-') : tone;
  const Icon = statusIcons[normalizedTone] || Circle;

  return (
    <span className={`alt4-status alt4-status--${normalizedTone}${className ? ` ${className}` : ''}`}>
      <Icon size={13} strokeWidth={2.4} aria-hidden="true" />
      {label}
    </span>
  );
}

export function WorkflowCard({ eyebrow, title, body, description, items = [], children, className = '' }) {
  return (
    <article className={`alt4-card alt4-workflow-card${className ? ` ${className}` : ''}`}>
      {eyebrow && <p className="alt4-eyebrow">{eyebrow}</p>}
      {title && <h3>{title}</h3>}
      {(body || description) && <p>{body || description}</p>}
      {items.length > 0 && (
        <ul className="alt4-check-list">
          {items.map((item) => (
            <li key={item}>
              <Check size={16} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {children}
    </article>
  );
}

export function ClosingCta({
  eyebrow = 'See the operating layer in motion',
  title = 'Let the PMS carry the work forward.',
  body = 'Walk through how Asha can coordinate work across locations while your team keeps clear control.',
  description,
}) {
  return (
    <section className="alt4-section alt4-closing-section" aria-labelledby="alt4-closing-title">
      <div className="alt4-container">
        <div className="alt4-closing-card">
          <div>
            <p className="alt4-eyebrow">{eyebrow}</p>
            <h2 id="alt4-closing-title">{title}</h2>
            <p>{body || description}</p>
          </div>
          <PrimaryCta />
        </div>
      </div>
    </section>
  );
}
